/**
 * General Utility Helpers
 * 
 * Contains utility functions for date handling, invoice number generation,
 * and other common operations. Currency-related functions have been moved
 * to currencyFormatter.js and calculations to calculations.js
 */

// ============================================================================
// DATE UTILITIES
// ============================================================================

/**
 * Get today's date in ISO format (YYYY-MM-DD)
 * @returns {string} Today's date in ISO format
 */
export function getTodayDate() {
  return new Date().toISOString().split('T')[0];
}

/**
 * Get a future date from today
 * @param {number} days - Number of days from today
 * @returns {string} Future date in ISO format
 */
export function getDueDateFromToday(days = 30) {
  const futureDate = new Date();
  futureDate.setDate(futureDate.getDate() + days);
  return futureDate.toISOString().split('T')[0];
}

/**
 * Format a date string for display
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {string} locale - Locale for formatting (default: 'en-US')
 * @returns {string} Formatted date string
 */
export function formatDateForDisplay(dateStr, locale = 'en-US') {
  if (!dateStr) return '';
  
  try {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

/**
 * Check if a date string is valid
 * @param {string} dateStr - Date string to validate
 * @returns {boolean} Whether the date is valid
 */
export function isValidDate(dateStr) {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  return date instanceof Date && !isNaN(date);
}

// ============================================================================
// INVOICE NUMBER UTILITIES
// ============================================================================
// DEPRECATED: Invoice numbers are now generated by the backend via /api/invoices/finalize
// These functions are kept for backwards compatibility but should not be used in new code.
// ============================================================================

/**
 * @deprecated Invoice numbers are assigned by backend during finalization.
 * Use finalizeInvoice() from api.js instead.
 * 
 * Generate a more descriptive invoice number with prefix and date
 * @param {string} prefix - Custom prefix (default: 'INV')
 * @param {boolean} includeDate - Include date in the format
 * @returns {string} Generated invoice number
 */
export function generateCustomInvoiceNumber(prefix = 'INV', includeDate = false) {
  console.warn('generateCustomInvoiceNumber is deprecated. Invoice numbers are assigned by backend.');
  const now = new Date();
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  
  if (includeDate) {
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    return `${prefix}-${year}${month}-${random}`;
  }
  
  const timestamp = Date.now().toString().slice(-6);
  return `${prefix}-${timestamp}`;
}

// ============================================================================
// STRING UTILITIES
// ============================================================================

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text
 */
export function truncateText(text, maxLength = 50) {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Capitalize first letter of each word
 * @param {string} text - Text to capitalize
 * @returns {string} Capitalized text
 */
export function capitalizeWords(text) {
  if (!text) return '';
  return text
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// ============================================================================
// OBJECT UTILITIES
// ============================================================================

/**
 * Deep clone an object (simple implementation)
 * @param {Object} obj - Object to clone
 * @returns {Object} Cloned object
 */
export function deepClone(obj) {
  if (obj === null || typeof obj !== 'object') return obj;
  
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch {
    return obj;
  }
}

/**
 * Check if two objects are deeply equal
 * @param {Object} obj1 - First object
 * @param {Object} obj2 - Second object
 * @returns {boolean} Whether objects are equal
 */
export function deepEqual(obj1, obj2) {
  if (obj1 === obj2) return true;
  if (!obj1 || !obj2) return false;
  
  try {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  } catch {
    return false;
  }
}

// ============================================================================
// DEBOUNCE UTILITY
// ============================================================================

/**
 * Create a debounced version of a function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce(func, wait = 300) {
  let timeoutId = null;
  
  return function debounced(...args) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = setTimeout(() => {
      func.apply(this, args);
      timeoutId = null;
    }, wait);
  };
}

const helpersExports = {
  getTodayDate,
  getDueDateFromToday,
  formatDateForDisplay,
  isValidDate,
  generateCustomInvoiceNumber,
  truncateText,
  capitalizeWords,
  deepClone,
  deepEqual,
  debounce,
};

export default helpersExports;